import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { secondaryText } from 'components/CallScribe/callScribeTheme';

const useCallTimerStyles = makeStyles((theme: Theme) => ({
  preCall: {
    ...theme.typography.body1,
    color: secondaryText(theme),
    minHeight: 20,
  },
  running: {
    ...theme.typography.h4,
    fontWeight: 300,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: 1,
    color: theme.palette.text.primary,
  },
  runningCompact: {
    ...theme.typography.body1,
    fontWeight: 400,
    letterSpacing: 0.5,
    color: secondaryText(theme),
  },
}));

export interface ICallTimerProps {
  running: boolean;
  preCallLabel?: string;
  compact?: boolean;
  className?: string;
  /** Epoch ms the call was answered. Without it the timer counts from mount. */
  sinceMs?: number;
}

const CallTimer: React.FC<ICallTimerProps> = ({
  running,
  preCallLabel,
  compact,
  className,
  sinceMs,
}) => {
  const classes = useCallTimerStyles();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!running) {
      setElapsed(0);
      return undefined;
    }
    const start = typeof sinceMs === 'number' ? sinceMs : Date.now();
    setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    const intervalId = window.setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    }, 500);
    return () => { window.clearInterval(intervalId); };
  }, [running, sinceMs]);

  const formatted = format(elapsed);

  if (!running) {
    return className ? null : <div className={classes.preCall}>{preCallLabel ?? ''}</div>;
  }

  if (className) {
    return <div className={className}>{formatted}</div>;
  }

  return (
    <div className={`${classes.running} ${compact ? classes.runningCompact : ''}`}>
      {formatted}
    </div>
  );
};

const format = (totalSeconds: number): string => {
  const pad = (n: number): string => n.toString().padStart(2, '0');
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${pad(minutes)}:${pad(seconds)}`;
};

export default CallTimer;
